/**
 * Keycloak provider wrapper for SpectraIQ admin portal.
 *
 * Sprint 6 — replaces the original Auth0 provider with self-managed
 * Keycloak. The realm is provisioned from infra/keycloak/realm-export.json
 * and contains a `spectraiq-admin-portal` public client (PKCE) and a
 * `spectraiq-backend` confidential client (audience the SPA targets).
 *
 * Configure via Vite env:
 *   VITE_KEYCLOAK_URL=http://localhost:8180
 *   VITE_KEYCLOAK_REALM=spectraiq
 *   VITE_KEYCLOAK_CLIENT_ID=spectraiq-admin-portal
 *
 * The component falls back to rendering children un-wrapped when the env
 * vars are missing so the mock-data UI keeps working in design-only mode.
 * That fallback applies ONLY to missing config. If the env vars are present
 * but init() fails (Keycloak down / unreachable / CORS), the provider shows
 * an explicit error screen rather than silently rendering the app signed-out.
 */

import Keycloak from 'keycloak-js';
import { useEffect, useRef, useState } from 'react';
import { setAuthTokenGetter, setAuthActions } from './api.js';

let _keycloak = null;
let _initStarted = false;
function getKeycloak({ url, realm, clientId }) {
  if (_keycloak) return _keycloak;
  _keycloak = new Keycloak({ url, realm, clientId });
  return _keycloak;
}

export function SpectraIqAuthProvider({ children }) {
  const url      = import.meta.env.VITE_KEYCLOAK_URL;
  const realm    = import.meta.env.VITE_KEYCLOAK_REALM;
  const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;

  // Dev / design-only mode: no Keycloak env => render children directly.
  // Deliberate, but noisy on purpose: this path renders the whole app with
  // no session at all, which is easy to mistake for a broken login.
  if (!url || !realm || !clientId) {
    console.warn(
      '[keycloak] design-only mode: VITE_KEYCLOAK_URL / _REALM / _CLIENT_ID not set. ' +
      'The app is rendering UNAUTHENTICATED and API calls will not carry a bearer token.'
    );
    return children;
  }

  const [ready, setReady] = useState(false);
  const [initError, setInitError] = useState(null);
  const kcRef = useRef(getKeycloak({ url, realm, clientId }));

  useEffect(() => {
    // Guard against React Strict Mode double-invoking effects in dev.
    // keycloak-js throws if init() is called more than once on the same
    // instance, so we track it with a module-level flag.
    if (_initStarted) return;
    _initStarted = true;

    const kc = kcRef.current;

    // init() returns a promise; once login is complete we publish a token
    // getter that lazily refreshes the access token when it expires within
    // the next 30 seconds. The Axios client in api.js calls it on every
    // request.
    kc.init({
      onLoad:                'login-required',
      checkLoginIframe:      false,
      pkceMethod:            'S256',
      enableLogging:         false,
    }).then((authenticated) => {
      if (authenticated) {
        setAuthTokenGetter(async () => {
          try {
            // updateToken(30) refreshes if the token expires in < 30s.
            await kc.updateToken(30);
            return kc.token;
          } catch {
            // Refresh failed (refresh token expired) — bounce to login.
            kc.login();
            return null;
          }
        });
        setAuthActions({
          // updateToken(-1) forces a refresh regardless of remaining validity.
          forceRefresh: async () => {
            try {
              await kc.updateToken(-1);
              return kc.token;
            } catch {
              return null;
            }
          },
          login: () => kc.login(),
        });
        // Proactive refresh: keycloak-js fires this when the access token
        // hits its expiry, even while the user is idle on the page. Without
        // it, the first request after expiry always pays a refresh
        // round-trip (or fails outright if the retry path regresses).
        kc.onTokenExpired = () => {
          kc.updateToken(30).catch(() => kc.login());
        };
      } else {
        setAuthTokenGetter(async () => null);
      }
      setReady(true);
    }).catch((err) => {
      console.error('[keycloak] init failed', err);
      // Do NOT render children here. Failing open makes an unreachable
      // Keycloak indistinguishable from design-only mode: the app renders
      // fully unauthenticated with no token, and every API call 401s with
      // no explanation. Surface the failure instead.
      setInitError(err);
      // Allow a retry: init() is guarded by the module-level _initStarted
      // flag, so the retry button does a full reload rather than re-init.
      _initStarted = false;
      setReady(true);
    });
  }, []);

  if (initError) {
    const detail = String(initError?.message || initError || 'unknown error');
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh', padding: '2rem' }}>
        <div style={{ maxWidth: 520, textAlign: 'center' }}>
          <h1 style={{ fontSize: '1.125rem', fontWeight: 600, marginBottom: '0.5rem' }}>
            Authentication unavailable
          </h1>
          <p className="text-sm text-muted" style={{ marginBottom: '0.75rem' }}>
            Could not reach the identity provider, so you have not been signed in.
          </p>
          <p className="mono text-xs text-muted" style={{ marginBottom: '1.25rem', wordBreak: 'break-all' }}>
            {url}/realms/{realm}
            <br />
            {detail}
          </p>
          <button type="button" onClick={() => window.location.reload()}>
            Retry
          </button>
        </div>
      </div>
    );
  }

  if (!ready) {
    return (
      <div style={{ display: 'grid', placeItems: 'center', height: '100vh' }}>
        <div className="mono text-xs text-muted">Authenticating…</div>
      </div>
    );
  }

  return children;
}

/**
 * Convenience hook for components that need the raw Keycloak instance
 * (e.g. to call logout, switch tenant, or read userinfo).
 *
 * Returns null when running in design-only mode.
 */
export function useKeycloak() {
  const url = import.meta.env.VITE_KEYCLOAK_URL;
  if (!url) return null;
  return _keycloak;
}
